import type { MessageLevel, TaskEvent, TaskId, Unit } from "./events.ts";

/**
 * The consuming side: the tasks and messages a stream of events describes,
 * kept as a tree for a renderer to read. One view can be fed from several
 * reporters, which is how the page shows a worker's tasks beside its own.
 */
export interface TaskState {
    readonly id: TaskId;
    readonly parent: TaskId | undefined;
    readonly label: string;
    readonly unit: Unit | undefined;
    readonly children: TaskState[];
    /** Undefined while the size of the work is unknown. */
    total: number | undefined;
    done: number;
    detail: string | undefined;
    state: "running" | "done" | "failed";
    error: string | undefined;
    startedAt: number;
    endedAt: number | undefined;
}

export interface Message {
    readonly level: MessageLevel;
    readonly text: string;
    readonly task: TaskId | undefined;
    readonly at: number;
}

export class TaskView {
    readonly #byId = new Map<TaskId, TaskState>();
    readonly #roots: TaskState[] = [];
    readonly #messages: Message[] = [];
    readonly #listeners = new Set<() => void>();

    /** Top-level tasks, in the order they started. */
    get roots(): readonly TaskState[] {
        return this.#roots;
    }

    get messages(): readonly Message[] {
        return this.#messages;
    }

    task(id: TaskId): TaskState | undefined {
        return this.#byId.get(id);
    }

    /** Every task still running, each parent before its children. */
    running(): TaskState[] {
        const found: TaskState[] = [];
        const walk = (tasks: readonly TaskState[]): void => {
            for (const task of tasks) {
                if (task.state === "running") found.push(task);
                walk(task.children);
            }
        };
        walk(this.#roots);
        return found;
    }

    apply(event: TaskEvent): void {
        switch (event.kind) {
            case "start": {
                const parent = event.parent === undefined ? undefined : this.#byId.get(event.parent);
                const task: TaskState = {
                    id: event.id,
                    parent: parent?.id,
                    label: event.label,
                    unit: event.unit,
                    children: [],
                    total: event.total,
                    done: 0,
                    detail: undefined,
                    state: "running",
                    error: undefined,
                    startedAt: Date.now(),
                    endedAt: undefined,
                };
                this.#byId.set(task.id, task);
                (parent?.children ?? this.#roots).push(task);
                break;
            }
            case "progress": {
                const task = this.#byId.get(event.id);
                if (task === undefined) break;
                task.done = event.done;
                if (event.total !== undefined) task.total = event.total;
                if (event.detail !== undefined) task.detail = event.detail;
                break;
            }
            case "end": {
                const task = this.#byId.get(event.id);
                if (task === undefined) break;
                task.state = event.error === undefined ? "done" : "failed";
                task.error = event.error;
                task.endedAt = Date.now();
                break;
            }
            case "message":
                this.#messages.push({
                    level: event.level,
                    text: event.text,
                    task: event.task,
                    at: Date.now(),
                });
                break;
        }
        for (const listener of this.#listeners) listener();
    }

    /** Call `listener` after every applied event. Returns how to stop. */
    onChange(listener: () => void): () => void {
        this.#listeners.add(listener);
        return () => this.#listeners.delete(listener);
    }
}
